import { cx } from '@linaria/core';
import { AccessCodes } from '@pm2/api';
import { useActiveRevision } from '@pm2/course-revisions';
import { centerCls } from '@pm2/react-utils';
import { pm2ConfigStore } from '@pm2/store';
import { ReliefSVG } from '@pm2/ui-components';
import { useAppContext } from '@provider/app-context';
import { useUserIdentityState } from '@provider/user-identity';
import { Link } from '@ui/kit';
import { PublisherLogo } from '@ui/kit/icons';
import { Chevron } from '@utils/react';
import { Column } from './column.casing.js';
import { svg, logo, logoWrapper, columnsWrapper, navigation, navItem, content, publisherLogo, publisherLogoWrapper, wrapper, modifiers } from './footer.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';

function Footer(props) {
  const user = useUserIdentityState(state => state.user);
  const publisher = useAppContext(state => state.publisher);
  const columns = pm2ConfigStore.useSelector(state => state.config.settings.footerColumns);
  const activeRevision = useActiveRevision(props.courseDescriptor?.id);
  const nextChapterDescriptor = props.showNavigation && props.courseDescriptor && getNextChapterDescriptor(user, activeRevision, props.courseDescriptor, props.pageDescriptor);
  const prevChapterDescriptor = (() => {
    const descriptor = props.showNavigation && props.courseDescriptor && getPrevChapterDescriptor(user, activeRevision, props.courseDescriptor, props.pageDescriptor);

    // if there's no previous chapter, then link to the frontpage of the course
    // (unless that page is already active)
    if (props.showNavigation && !descriptor && props.pageDescriptor.id !== props.courseDescriptor?.id) {
      return props.courseDescriptor;
    }
    return descriptor;
  })();
  return /*#__PURE__*/jsxs("footer", {
    ref: props.footerRef,
    className: cx(wrapper, props.footerRef && props.showColumn && modifiers.footerHasColumns.className),
    children: [/*#__PURE__*/jsx(ReliefSVG, {
      position: "bottom",
      className: svg
    }), /*#__PURE__*/jsxs("div", {
      className: cx(centerCls, content),
      children: [publisher !== "louisiana" && props.footerRef && props.showColumn && /*#__PURE__*/jsx("div", {
        className: logo,
        children: /*#__PURE__*/jsx("span", {
          className: logoWrapper,
          children: /*#__PURE__*/jsx(PublisherLogo, {
            preserveAspectRatio: "xMinYMin"
          })
        })
      }), props.showColumn && props.footerRef && /*#__PURE__*/jsx("div", {
        className: columnsWrapper,
        children: columns.map((column, index) => /*#__PURE__*/jsx(Column, {
          column: column
        }, index))
      }), props.showNavigation && /*#__PURE__*/jsxs("div", {
        className: navigation,
        children: [prevChapterDescriptor && /*#__PURE__*/jsxs(Link, {
          href: prevChapterDescriptor.path,
          theme: "primary",
          className: navItem,
          children: [/*#__PURE__*/jsx(Chevron, {
            size: 7,
            lineWidth: 1.5,
            inversed: true
          }), "Forrige side"]
        }), nextChapterDescriptor && /*#__PURE__*/jsxs(Link, {
          href: nextChapterDescriptor.path,
          theme: "primary",
          className: navItem,
          children: ["N\xE6ste side", /*#__PURE__*/jsx(Chevron, {
            size: 7,
            lineWidth: 1.5
          })]
        })]
      })]
    }), publisher !== "louisiana" && /*#__PURE__*/jsx("div", {
      className: cx(centerCls, publisherLogoWrapper),
      children: /*#__PURE__*/jsx("div", {
        className: publisherLogo,
        children: /*#__PURE__*/jsx(PublisherLogo, {
          small: true
        })
      })
    })]
  });
}

/**
 * search through the navigation tree, and attempt to locate the next chapter
 * descriptor.
 */
function getNextChapterDescriptor(user, revision, courseDescriptor, currPageDescriptor) {
  // if the course frontpage is currently open, then next page is equal to the
  // first child within the course
  if (currPageDescriptor?.id === courseDescriptor.id) {
    if (!courseDescriptor.children) {
      return undefined;
    }
    if (isPageAvailable(courseDescriptor.children[0], revision, user)) {
      return unwrapNextChapterDescriptor(user, revision, courseDescriptor, courseDescriptor.children[0]);
    }
    return getNextChapterDescriptor(user, revision, courseDescriptor, courseDescriptor.children[0]);
  }
  if (!currPageDescriptor?.parentId) {
    return undefined;
  }

  // ... otherwise attempt to find the next direct sibling of the current page
  // directly on it's parent (either the course or a nested chapter group)
  const parentDescriptor = getParentDescriptor(courseDescriptor, currPageDescriptor);
  const nextSiblingDescriptor = getNextSiblingDescriptor(user, revision, parentDescriptor, currPageDescriptor);
  if (nextSiblingDescriptor) {
    return unwrapNextChapterDescriptor(user, revision, courseDescriptor, nextSiblingDescriptor);
  }

  // if the current page is nested inside a chapter group and we couldn't find
  // a direct sibling, then go to next sibling from the chapter group
  if (parentDescriptor?.type === "ChapterGroup" && !revision?.disabledContentIds[parentDescriptor.id]?.disabled) {
    return unwrapNextChapterDescriptor(user, revision, courseDescriptor, getNextSiblingDescriptor(user, revision, courseDescriptor, parentDescriptor));
  }
  return undefined;
}

/**
 * finds the next sibling (if any exists) for the given page descriptor inside
 * it's parent descriptor.
 */
function getNextSiblingDescriptor(user, revision, parentDescriptor, pageDescriptor) {
  if (!parentDescriptor || !parentDescriptor.children) {
    return undefined;
  }
  let currIndex = parentDescriptor.children.findIndex(it => it.id === pageDescriptor.id);
  while (currIndex !== -1 && currIndex + 1 < parentDescriptor.children.length) {
    const candidateDescriptor = parentDescriptor.children[currIndex + 1];
    if (isPageAvailable(candidateDescriptor, revision, user)) {
      return candidateDescriptor;
    }
    currIndex++;
  }
  return undefined;
}

/**
 * unwraps the next page descriptor to ensure that a chapter descriptor is
 * going to be returned (if a chapter group was given, then find the first
 * available chapter inside it or continue to the next sibling of that chapter
 * group).
 */
function unwrapNextChapterDescriptor(user, revision, courseDescriptor, nextPageDescriptor) {
  if (nextPageDescriptor === undefined) {
    return;
  }
  if (nextPageDescriptor.type === "ChapterGroup" && !revision?.disabledContentIds[nextPageDescriptor.id]?.disabled) {
    const validDescriptorInChapterGroup = nextPageDescriptor.children?.find(child => isPageAvailable(child, revision, user));
    return validDescriptorInChapterGroup ? validDescriptorInChapterGroup : getNextChapterDescriptor(user, revision, courseDescriptor, nextPageDescriptor);
  }
  return nextPageDescriptor;
}

/**
 * search through the navigation tree, and attempt to locate the previous
 * chapter descriptor.
 */
function getPrevChapterDescriptor(user, revision, courseDescriptor, currPageDescriptor) {
  // if the course frontpage is currently open, then there is no previous page
  // available
  if (currPageDescriptor.id === courseDescriptor.id) {
    return undefined;
  }
  if (!currPageDescriptor.parentId) {
    return undefined;
  }

  // ... otherwise attempt to find the next direct sibling of the current page
  // directly on it's parent (either the course or a nested chapter group)
  const parentDescriptor = getParentDescriptor(courseDescriptor, currPageDescriptor);
  const prevSiblingDescriptor = getPrevSiblingDescriptor(user, revision, parentDescriptor, currPageDescriptor);
  if (prevSiblingDescriptor) {
    return unwrapPrevChapterDescriptor(user, revision, courseDescriptor, prevSiblingDescriptor);
  }

  // if the current page is nested inside a chapter group and we couldn't find
  // a direct sibling, then go to previous sibling from the chapter group
  if (parentDescriptor?.type === "ChapterGroup" && !revision?.disabledContentIds[parentDescriptor.id]?.disabled) {
    return unwrapPrevChapterDescriptor(user, revision, courseDescriptor, getPrevSiblingDescriptor(user, revision, courseDescriptor, parentDescriptor));
  }

  // if we get here, there is no previous chapter - default to showing the
  // course frontpage link, provided that's not already active
  return undefined;
}

/**
 * finds the previous sibling (if any exists) for the given page descriptor
 * inside it's parent descriptor.
 */
function getPrevSiblingDescriptor(user, revision, parentDescriptor, pageDescriptor) {
  if (!parentDescriptor || !parentDescriptor.children) {
    return undefined;
  }
  let currIndex = parentDescriptor.children.findIndex(it => it.id === pageDescriptor.id);
  while (currIndex > 0) {
    const candidateDescriptor = parentDescriptor.children[currIndex - 1];
    if (isPageAvailable(candidateDescriptor, revision, user)) {
      return candidateDescriptor;
    }
    currIndex--;
  }
  return undefined;
}

/**
 * unwraps the previous page descriptor to ensure that a chapter descriptor is
 * going to be returned (if a chapter group was given, then find the first
 * available chapter inside it or continue to the next sibling of that chapter
 * group).
 */
function unwrapPrevChapterDescriptor(user, revision, courseDescriptor, prevPageDescriptor) {
  if (prevPageDescriptor === undefined) {
    return;
  }
  if (prevPageDescriptor.type === "ChapterGroup" && !revision?.disabledContentIds[prevPageDescriptor.id]?.disabled) {
    const validDescriptorInChapterGroup = [...(prevPageDescriptor.children ?? [])].reverse()?.find(child => isPageAvailable(child, revision, user));
    return validDescriptorInChapterGroup ? validDescriptorInChapterGroup : getPrevChapterDescriptor(user, revision, courseDescriptor, prevPageDescriptor);
  }
  return prevPageDescriptor;
}

/**
 * find reference to the parent descriptor for the given page (this can either
 * be the course itself, or a chapter group nested directly under the course).
 */
function getParentDescriptor(courseDescriptor, pageDescriptor) {
  if (pageDescriptor.parentId === courseDescriptor.id) {
    return courseDescriptor;
  }
  return courseDescriptor.children?.find(it => it.id === pageDescriptor.parentId);
}

/**
 * determines whether or not the provided page should be considered available
 * in the current frontend context.
 */
function isPageAvailable(pageDescriptor, revision, user) {
  const hasBasicAccess = pageDescriptor?.accessCode === AccessCodes.ALLOWED && !pageDescriptor.hidden;
  const hasTeacherGuideAccess = pageDescriptor?.type !== "TeachersGuidePage" || user?.role === "teacher";
  const isDisabledInRevision = revision?.disabledContentIds[pageDescriptor?.id ?? ""]?.disabled;
  return hasBasicAccess && hasTeacherGuideAccess && !isDisabledInRevision;
}

export { Footer };
//# sourceMappingURL=footer.casing.js.map
